import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Building2,
  ExternalLink,
  Plus,
  Trash2,
  Link2,
  Check,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/**
 * Banner shown on a CGI project's detail page that surfaces the
 * Support Hub auto-link flow. Two states:
 *   1. Already linked → green "this project's deployment lives at X"
 *      with a deep-link to /settings/support-products/{slug}.
 *   2. Eligible (project has Spinhouse/Vexos services) but not linked
 *      → orange CTA → opens dialog → admin enters URL + optional staging
 *      mirror → POST register-from-project.
 *
 * No-ops if the eligibility endpoint says the project doesn't match any
 * product (most CGI projects don't ship deployable software).
 */
const SUPPORT_HUB_WRITE_ROLES = new Set(["admin"]);

const PRODUCT_LABEL = {
  hub: "HUB",
  spinhouse: "Spinhouse",
  vexos: "Vexos",
};

export default function SupportHubProjectLink({ project }) {
  const { user } = useAuth();
  const [eligibility, setEligibility] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    product_slug: "",
    label: "",
    url: "",
    notes: "",
    mirrors: [],
  });

  const fetchEligibility = useCallback(async () => {
    if (!project?.id) return;
    try {
      setLoading(true);
      const { data } = await api.get(`/support-products/eligibility-for-project/${project.id}`);
      setEligibility(data);
    } catch (e) {
      // Silent — most projects won't have Support Hub eligibility, no need to alarm.
      setEligibility(null);
    } finally {
      setLoading(false);
    }
  }, [project?.id]);

  useEffect(() => { fetchEligibility(); }, [fetchEligibility]);

  if (loading || !eligibility) return null;
  if (!eligibility.is_eligible) return null;

  const canWrite = SUPPORT_HUB_WRITE_ROLES.has(user?.role);
  const existing = eligibility.existing_instance;

  // ---------- Already linked ----------
  if (existing) {
    return (
      <Card className="bg-emerald-950/30 border-emerald-700/40 mb-6">
        <CardContent className="p-4 flex items-start gap-3">
          <div className="mt-0.5">
            <div className="rounded-full bg-emerald-500/15 p-2">
              <Check className="h-4 w-4 text-emerald-300" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm font-medium text-emerald-200">
                Linked to Support Hub
              </span>
              <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30 text-[10px] uppercase">
                {PRODUCT_LABEL[existing.product_slug] || existing.product_slug}
              </Badge>
              <Badge className="bg-zinc-800 text-zinc-300 border-zinc-700 text-[10px] font-mono">
                {existing.instance_label || existing.instance_slug}
              </Badge>
            </div>
            {existing.url ? (
              <div className="mt-1.5 text-xs text-zinc-400 truncate">
                <a
                  href={existing.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-emerald-300/80 hover:text-emerald-200"
                >
                  {existing.url}
                </a>
              </div>
            ) : null}
            <p className="mt-1.5 text-[11px] text-zinc-500">
              Customers reporting issues from this site can find their session in {" "}
              <Link
                to="/settings/support-chats"
                className="underline hover:text-zinc-300"
              >
                Support Chats
              </Link>
              {" "}filtered by deployment.
            </p>
          </div>
          <Link
            to={`/settings/support-products/${existing.product_slug}`}
            className="inline-flex items-center gap-1 text-xs text-zinc-400 hover:text-zinc-50"
          >
            Manage <ExternalLink className="h-3 w-3" />
          </Link>
        </CardContent>
      </Card>
    );
  }

  // ---------- Eligible but not linked yet ----------
  const openDialog = () => {
    setForm({
      product_slug: eligibility.suggested_product_slug || "spinhouse",
      label: eligibility.project?.name || "",
      url: "",
      notes: "",
      mirrors: [],
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    if (!form.url.trim()) {
      toast.error("Production URL is required");
      return;
    }
    try {
      setSubmitting(true);
      await api.post(`/support-products/instances/register-from-project`, {
        project_id: project.id,
        product_slug: form.product_slug,
        label: form.label.trim() || project.name,
        url: form.url.trim(),
        notes: form.notes.trim(),
        mirror_urls: form.mirrors
          .map((m) => ({
            label: (m.label || "").trim(),
            url: (m.url || "").trim(),
            notes: (m.notes || "").trim(),
          }))
          .filter((m) => m.url),
      });
      toast.success("Deployment registered with Support Hub");
      setDialogOpen(false);
      fetchEligibility();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.error === "already_linked") {
        toast.warning("Project is already linked. Refresh to see.");
        fetchEligibility();
      } else {
        toast.error(typeof detail === "string" ? detail : "Failed to register deployment");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Card className="bg-orange-950/20 border-orange-700/40 mb-6">
        <CardContent className="p-4 flex items-start gap-3">
          <div className="mt-0.5">
            <div className="rounded-full bg-orange-500/15 p-2">
              <Building2 className="h-4 w-4 text-orange-300" />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-orange-200">
              This project ships a {PRODUCT_LABEL[eligibility.suggested_product_slug] || "Support Hub"} deployment
            </div>
            <p className="mt-1 text-xs text-zinc-400">
              Register the production URL so customers can ask questions about this specific
              site through the embed widget, and so we can trace issues back to this project later.
            </p>
            <div className="mt-2 flex flex-wrap gap-1">
              {(eligibility.service_names || [])
                .filter((n) => /spinhouse|vexos|devbase/i.test(n))
                .slice(0, 4)
                .map((n) => (
                  <Badge key={n} className="bg-zinc-800 text-zinc-400 border-zinc-700 text-[10px]">
                    {n}
                  </Badge>
                ))}
            </div>
          </div>
          {canWrite ? (
            <Button
              size="sm"
              className="bg-orange-500 hover:bg-orange-600 text-white"
              onClick={openDialog}
            >
              <Link2 className="h-3.5 w-3.5 mr-1.5" /> Register deployment
            </Button>
          ) : (
            <span className="text-[10px] text-zinc-500 italic max-w-[140px] text-right">
              Ask an admin to register this deployment
            </span>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-700 max-w-xl">
          <DialogHeader>
            <DialogTitle className="text-zinc-100">Register deployment with Support Hub</DialogTitle>
            <DialogDescription className="text-zinc-400 text-xs">
              Creates a new instance under the chosen product and links it to this CGI project.
              The instance shows up in the chat picker and can carry an embed widget API key.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-xs">
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Product</label>
              <div className="flex gap-2 mt-1">
                {["spinhouse", "vexos"].map((slug) => (
                  <button
                    key={slug}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, product_slug: slug }))}
                    className={`flex-1 rounded border px-3 py-2 text-xs font-medium transition-colors ${
                      form.product_slug === slug
                        ? "border-orange-500 bg-orange-500/10 text-orange-200"
                        : "border-zinc-700 bg-zinc-800/50 text-zinc-400 hover:bg-zinc-800"
                    }`}
                  >
                    {PRODUCT_LABEL[slug]}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Deployment label</label>
              <Input
                placeholder="e.g. BI Global, Avalon"
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                className="bg-zinc-900 border-zinc-700 text-zinc-100 h-8 text-xs mt-1"
              />
              <p className="text-[10px] text-zinc-600 mt-0.5">
                Defaults to the project name. Shown in the chat picker.
              </p>
            </div>
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Production URL *</label>
              <Input
                placeholder="https://bi-global.spinhouse.ai"
                value={form.url}
                onChange={(e) => setForm((f) => ({ ...f, url: e.target.value }))}
                className="bg-zinc-900 border-zinc-700 text-zinc-100 h-8 text-xs font-mono mt-1"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase text-zinc-500">Notes (AI sees this)</label>
              <Textarea
                placeholder="Custom integrations, known quirks, …"
                value={form.notes}
                onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                className="bg-zinc-900 border-zinc-700 text-zinc-100 text-xs mt-1 min-h-[60px]"
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[10px] uppercase text-zinc-500">
                  Mirrors / staging URLs ({form.mirrors.length})
                </label>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-6 text-[10px] border-zinc-700"
                  onClick={() =>
                    setForm((f) => ({
                      ...f,
                      mirrors: [...f.mirrors, { label: "", url: "", notes: "" }],
                    }))
                  }
                >
                  <Plus className="h-3 w-3 mr-0.5" /> Add mirror
                </Button>
              </div>
              {form.mirrors.length === 0 ? (
                <p className="text-[10px] text-zinc-600 italic">
                  Add staging or alt-domain URLs if any. Optional.
                </p>
              ) : (
                <div className="space-y-1.5">
                  {form.mirrors.map((m, idx) => (
                    <div key={idx} className="flex gap-1">
                      <Input
                        placeholder="staging"
                        value={m.label}
                        onChange={(e) => {
                          const next = [...form.mirrors];
                          next[idx] = { ...next[idx], label: e.target.value };
                          setForm((f) => ({ ...f, mirrors: next }));
                        }}
                        className="bg-zinc-900 border-zinc-700 text-zinc-100 h-7 text-[11px] w-28"
                      />
                      <Input
                        placeholder="https://staging.example.com"
                        value={m.url}
                        onChange={(e) => {
                          const next = [...form.mirrors];
                          next[idx] = { ...next[idx], url: e.target.value };
                          setForm((f) => ({ ...f, mirrors: next }));
                        }}
                        className="bg-zinc-900 border-zinc-700 text-zinc-100 h-7 text-[11px] font-mono flex-1"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setForm((f) => ({
                            ...f,
                            mirrors: f.mirrors.filter((_, i) => i !== idx),
                          }));
                        }}
                        className="px-1.5 rounded hover:bg-red-500/20 text-zinc-500 hover:text-red-300"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="border-zinc-700" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              className="bg-orange-500 hover:bg-orange-600"
              disabled={submitting || !form.url.trim()}
              onClick={submit}
            >
              {submitting ? "Registering…" : "Register"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
